import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CATEGORIES } from '../data/categories';
import { SCENARIOS } from '../data/scenarios';
import type { Category, ScenarioCard, CategoryId, AgeGroup } from '../types';

interface ContentState {
  categories: Category[];
  scenarios: ScenarioCard[];

  addCategory: (cat: Omit<Category, 'id'>) => void;
  updateCategory: (id: string, updates: Partial<Omit<Category, 'id'>>) => void;
  deleteCategory: (id: string) => void;

  addScenario: (scenario: Omit<ScenarioCard, 'id' | 'createdAt'>) => void;
  updateScenario: (id: string, updates: Partial<ScenarioCard>) => void;
  deleteScenario: (id: string) => void;
}

export const useContentStore = create<ContentState>()(
  persist(
    (set) => ({
      // On first load these are the seed arrays; subsequent loads use localStorage
      categories: CATEGORIES,
      scenarios: SCENARIOS,

      addCategory: (cat) => {
        const id = ('cat-' + Date.now()) as CategoryId;
        set((s) => ({
          categories: [...s.categories, { ...cat, id }],
        }));
      },

      updateCategory: (id, updates) => {
        set((s) => ({
          categories: s.categories.map((c) =>
            c.id === id ? { ...c, ...updates } : c
          ),
        }));
      },

      deleteCategory: (id) => {
        set((s) => ({
          categories: s.categories.filter((c) => c.id !== id),
        }));
      },

      addScenario: (scenario) => {
        const id = 'scn-' + Date.now();
        set((s) => ({
          scenarios: [
            ...s.scenarios,
            { ...scenario, id, createdAt: new Date().toISOString() },
          ],
        }));
      },

      updateScenario: (id, updates) => {
        set((s) => ({
          scenarios: s.scenarios.map((sc) =>
            sc.id === id ? { ...sc, ...updates } : sc
          ),
        }));
      },

      deleteScenario: (id) => {
        set((s) => ({
          scenarios: s.scenarios.filter((sc) => sc.id !== id),
        }));
      },
    }),
    { name: 'lifeskills-content-store' }
  )
);

// ─── Hooks for reactive data access ───────────────────────────────────────────
export const useCategories = () => useContentStore((s) => s.categories);
export const useScenarios = () => useContentStore((s) => s.scenarios);

// ─── Non-reactive getters (for page-load / navigation-triggered renders) ─────
export const getCategory = (id: string): Category | undefined =>
  useContentStore.getState().categories.find((c) => c.id === id);

export const getScenario = (id: string): ScenarioCard | undefined =>
  useContentStore.getState().scenarios.find((s) => s.id === id);

export const getScenariosByCategory = (categoryId: string): ScenarioCard[] =>
  useContentStore.getState().scenarios.filter((s) => s.categoryId === categoryId);

export const getFeaturedScenario = (): ScenarioCard => {
  const scenarios = useContentStore.getState().scenarios;
  return scenarios[new Date().getDate() % scenarios.length];
};

export const getScenariosByAge = (ageGroup: AgeGroup): ScenarioCard[] => {
  const scenarios = useContentStore.getState().scenarios;
  return scenarios.filter(
    (s) => s.ageGroup === ageGroup || s.ageGroup === 'all'
  );
};
